import { useState, useRef, useCallback, useEffect } from 'react';
import { getDiagramById, updateDiagram, createDiagram, publishDiagram } from '../api/diagramsApi';
import { bboxOf } from '../utils/canvasUtils';

export const useDiagramOperations = ({
    initialDiagramId,
    projectId = null,
    elements,
    setElements,
    clearSelection,
    zoom,
    setZoom,
    containerRef,
    canvasRef,
    userModifiedRef,
    fitBBoxInView,
    theme,
    mode = null,
    onDiagramLoaded = null,
    onDiagramLoadFailed = null,
}) => {
    const [currentDiagramId, setCurrentDiagramId] = useState(null);
    const [currentDiagramMeta, setCurrentDiagramMeta] = useState(null);
    const [diagramGitlabPath, setDiagramGitlabPath] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);
    const [code, setCode] = useState('');
    const loadReqIdRef = useRef(0);
    const diagramIdentityRef = useRef(null);
    diagramIdentityRef.current = `${projectId || 'local'}:${currentDiagramId || 'new'}:${mode || 'diagram'}`;

    // Получение позиции скролла для сохранения
    const getScrollPosition = useCallback(() => {
        const c = containerRef.current;
        if (!c) return { left: 0, top: 0 };
        return { left: c.scrollLeft, top: c.scrollTop };
    }, [containerRef]);

    // Загрузка диаграммы по ID
    const handleLoadDiagram = useCallback((diagram, diagramId = null) => {
        try {
            clearSelection();
            setElements(diagram.elements && Array.isArray(diagram.elements) ? diagram.elements : []);
            setCurrentDiagramId(diagramId);
            setCurrentDiagramMeta(diagram.meta || null);
            setDiagramGitlabPath(diagram.gitlab_path || null);
            setCode((diagram.meta && diagram.meta.code) || '');
            userModifiedRef.current = false;

            setTimeout(() => {
                if (diagram.elements && diagram.elements.length) {
                    const bb = bboxOf(diagram.elements);
                    if (bb) fitBBoxInView(bb);
                }
            }, 50);
        } catch (e) {
            console.error('Ошибка загрузки диаграммы:', e);
            alert('Ошибка загрузки диаграммы: ' + e.message);
        }
    }, [clearSelection, setElements, fitBBoxInView, userModifiedRef]);

    // Функция сохранения диаграммы
    const handleSaveDiagram = useCallback(async ({
        silent = false,
        extraMeta = null,
        metaOverride = null,
        preserveDirty = false,
        suppressErrorAlert = false,
    } = {}) => {
        if (isSaving) return;
        const requestIdentity = diagramIdentityRef.current;

        setIsSaving(true);
        try {
            const requestedSyncStatus = extraMeta?.sync_status || metaOverride?.sync_status || null;
            const diagramData = {
                meta: {
                    ...(currentDiagramMeta || {
                        name: 'Новая диаграмма',
                        domain: 'Architecture Vision',
                        version: 1,
                        createdAt: new Date().toISOString(),
                    }),
                    ...(metaOverride || {}),
                    code,
                    // Always stamp the type from the current board mode so that
                    // diagrams saved with a wrong type (e.g. 'uml' via old
                    // DiagramModal bug) are self-healed on the next Ctrl+S.
                    ...(mode ? { type: mode } : {}),
                    ...(extraMeta || {}),
                    updatedAt: new Date().toISOString(),
                },
                view: {
                    zoom,
                    scroll: getScrollPosition(),
                },
                elements,
                ...(requestedSyncStatus ? { sync_status: requestedSyncStatus } : {}),
            };

            // Store sourceCode in meta so backend can write native files to GitLab
            const diagramType = currentDiagramMeta?.type || mode;
            if ((diagramType === 'uml' || diagramType === 'erd' || diagramType === 'c4' || diagramType === 'free') && code) {
                diagramData.meta = { ...(diagramData.meta || {}), sourceCode: code };
            }
            if (diagramType === 'erd' && elements.length > 0) {
                const erdLayout = {};
                elements.forEach((el) => {
                    if (el._semanticId && typeof el.x === 'number') {
                        erdLayout[el._semanticId] = { x: el.x, y: el.y };
                    }
                });
                diagramData.meta = { ...(diagramData.meta || {}), erdLayout };
            }

            // For C4/free/whiteboard, generate PNG from SVG canvas using elements bounding box
            if ((diagramType === 'c4' || diagramType === 'free' || diagramType === 'whiteboard' || !diagramType) && canvasRef?.current && elements.length > 0) {
                try {
                    const bb = bboxOf(elements);
                    if (bb) {
                        const padding = 40;
                        const vx = bb.minX - padding;
                        const vy = bb.minY - padding;
                        const vw = (bb.maxX - bb.minX) + padding * 2;
                        const vh = (bb.maxY - bb.minY) + padding * 2;

                        const MAX_PX = 2048;
                        const scale = Math.min(1, MAX_PX / Math.max(vw, vh));
                        const pw = Math.round(vw * scale);
                        const ph = Math.round(vh * scale);

                        const svgEl = canvasRef.current;
                        const cloned = svgEl.cloneNode(true);
                        cloned.setAttribute('viewBox', `${vx} ${vy} ${vw} ${vh}`);
                        cloned.setAttribute('width', pw);
                        cloned.setAttribute('height', ph);
                        cloned.style.transform = 'none';

                        let svgData = new XMLSerializer().serializeToString(cloned);
                        if (!svgData.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
                            svgData = svgData.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
                        }

                        const canvas = document.createElement('canvas');
                        canvas.width = pw;
                        canvas.height = ph;
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        await new Promise((resolve) => {
                            img.onload = () => { ctx.drawImage(img, 0, 0); resolve(); };
                            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                        });
                        diagramData.png_preview = canvas.toDataURL('image/png').split(',')[1];
                    }
                } catch (e) {
                    console.warn('[Save] PNG generation failed:', e);
                }
            }

            if (currentDiagramId) {
                const updated = await updateDiagram(projectId, currentDiagramId, diagramData);
                if (diagramIdentityRef.current === requestIdentity) setCurrentDiagramMeta(updated.meta);
                console.log('Диаграмма сохранена:', updated.id);
                if (!silent) alert('Диаграмма успешно сохранена!');
                if (!preserveDirty && diagramIdentityRef.current === requestIdentity) userModifiedRef.current = false;
                return updated;
            } else {
                const created = await createDiagram(projectId, diagramData);
                if (diagramIdentityRef.current === requestIdentity) {
                    setCurrentDiagramId(created.id);
                    setCurrentDiagramMeta(created.meta);
                }
                console.log('Создана новая диаграмма:', created.id);
                if (!silent) alert('Диаграмма успешно создана!');
                if (!preserveDirty && diagramIdentityRef.current === requestIdentity) userModifiedRef.current = false;
                return created;
            }
        } catch (error) {
            console.error('Ошибка сохранения диаграммы:', error);
            if (!suppressErrorAlert) {
                alert('Ошибка сохранения диаграммы: ' + error.message);
            }
            throw error;
        } finally {
            setIsSaving(false);
        }
    }, [projectId, currentDiagramId, currentDiagramMeta, elements, zoom, isSaving, getScrollPosition, code, userModifiedRef, canvasRef, mode]);

    // Публикация диаграммы в GitLab
    // selectedEntry: { id, code, registry_type: { slug } } — optional, from DiagramPublishModal
    const handlePublishDiagram = useCallback(async (selectedEntry, { fitnessOverride = false } = {}) => {
        if (isPublishing) return;
        const requestIdentity = diagramIdentityRef.current;
        setIsPublishing(true);
        try {
            const diagramType = mode || currentDiagramMeta?.type;

            // If the diagram has never been saved, create it first to get a platform ID.
            let diagramId = currentDiagramId;
            if (!diagramId) {
                const newDoc = {
                    meta: {
                        ...(currentDiagramMeta || {
                            name: 'Новая диаграмма',
                            domain: 'Architecture Vision',
                            version: 1,
                            createdAt: new Date().toISOString(),
                            ...(mode ? { type: mode } : {}),
                        }),
                        code,
                        ...((diagramType === 'uml' || diagramType === 'c4') && code ? { sourceCode: code } : {}),
                    },
                    view: { zoom, scroll: getScrollPosition() },
                    elements,
                };
                const created = await createDiagram(projectId, newDoc);
                if (diagramIdentityRef.current === requestIdentity) {
                    setCurrentDiagramId(created.id);
                    setCurrentDiagramMeta(created.meta);
                }
                diagramId = created.id;
                if (diagramIdentityRef.current === requestIdentity) userModifiedRef.current = false;
            }

            const options = {};
            if (selectedEntry?.code) {
                options.rootEntryCode = selectedEntry.code;
                options.rootRegistrySlug = selectedEntry.registry_type?.slug || selectedEntry.registryType?.slug || '';
            }
            if (fitnessOverride) {
                options.fitnessOverride = true;
            }

            // Generate PNG preview from SVG canvas using elements bounding box
            if (canvasRef?.current && elements.length > 0) {
                try {
                    const bb = bboxOf(elements);
                    if (bb) {
                        const padding = 40;
                        const vx = bb.minX - padding;
                        const vy = bb.minY - padding;
                        const vw = (bb.maxX - bb.minX) + padding * 2;
                        const vh = (bb.maxY - bb.minY) + padding * 2;

                        const MAX_PX = 2048;
                        const scale = Math.min(1, MAX_PX / Math.max(vw, vh));
                        const pw = Math.round(vw * scale);
                        const ph = Math.round(vh * scale);

                        const svgEl = canvasRef.current;
                        const cloned = svgEl.cloneNode(true);
                        cloned.setAttribute('viewBox', `${vx} ${vy} ${vw} ${vh}`);
                        cloned.setAttribute('width', pw);
                        cloned.setAttribute('height', ph);
                        cloned.style.transform = 'none';

                        let svgData = new XMLSerializer().serializeToString(cloned);
                        if (!svgData.match(/^<svg[^>]+xmlns="http:\/\/www\.w3\.org\/2000\/svg"/)) {
                            svgData = svgData.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
                        }

                        const canvas = document.createElement('canvas');
                        canvas.width = pw;
                        canvas.height = ph;
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        await new Promise((resolve) => {
                            img.onload = () => { ctx.drawImage(img, 0, 0); resolve(); };
                            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                        });
                        options.pngPreview = canvas.toDataURL('image/png').split(',')[1];
                    }
                } catch (e) {
                    console.warn('[Publish] PNG generation failed:', e);
                }
            }

            // Pre-sync: write sourceCode to DB before publish so the backend reads the correct
            // source text when generating the native file. Without this, publish reads stale meta.
            if ((diagramType === 'uml' || diagramType === 'erd' || diagramType === 'c4') && code) {
                try {
                    const preSave = {
                        meta: { ...(currentDiagramMeta || {}), code, sourceCode: code, ...(mode ? { type: mode } : {}) },
                        view: { zoom, scroll: getScrollPosition() },
                        elements,
                    };
                    await updateDiagram(projectId, diagramId, preSave);
                } catch (preSyncErr) {
                    console.warn('[Publish] Pre-sync failed:', preSyncErr);
                }
            }

            await publishDiagram(projectId, diagramId, options);

            // Post-sync: ensure platform DB reflects published state for all diagram types.
            try {
                const saveData = {
                    meta: { ...(currentDiagramMeta || {}), code, ...(mode ? { type: mode } : {}) },
                    view: { zoom, scroll: getScrollPosition() },
                    elements,
                };
                if ((diagramType === 'uml' || diagramType === 'erd' || diagramType === 'c4') && code) {
                    saveData.meta = { ...saveData.meta, sourceCode: code };
                }
                await updateDiagram(projectId, diagramId, saveData);
                if (diagramIdentityRef.current === requestIdentity) userModifiedRef.current = false;
            } catch (syncErr) {
                console.warn('[Publish] Post-sync failed:', syncErr);
            }

            alert('Диаграмма опубликована в GitLab!');
        } catch (error) {
            if (error?.isFitnessWarnings) {
                throw error;
            }
            console.error('Ошибка публикации диаграммы:', error);
            alert('Ошибка публикации: ' + error.message);
        } finally {
            setIsPublishing(false);
        }
    }, [projectId, currentDiagramId, isPublishing, canvasRef, currentDiagramMeta, elements, zoom, code, getScrollPosition, userModifiedRef, mode]);

    // Экспорт изображения
    const handleExportImage = useCallback((format = 'svg') => {
        if (!elements.length) {
            alert("Нет элементов для экспорта");
            return;
        }

        const bb = bboxOf(elements);
        if (!bb) return;

        const padding = 20;
        const x = bb.minX - padding;
        const y = bb.minY - padding;
        const w = (bb.maxX - bb.minX) + 2 * padding;
        const h = (bb.maxY - bb.minY) + 2 * padding;

        const svgEl = canvasRef.current;
        if (!svgEl) return;

        // Клонируем SVG для манипуляций
        const clonedSvg = svgEl.cloneNode(true);

        // Устанавливаем ViewBox по границам контента
        clonedSvg.setAttribute('viewBox', `${x} ${y} ${w} ${h}`);
        clonedSvg.setAttribute('width', w);
        clonedSvg.setAttribute('height', h);
        clonedSvg.style.transform = 'none'; // Убираем зум

        // Сериализация
        const serializer = new XMLSerializer();
        let source = serializer.serializeToString(clonedSvg);

        // Добавляем namespaces
        if (!source.match(/^<svg[^>]+xmlns="http\:\/\/www\.w3\.org\/2000\/svg"/)) {
            source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
        }

        if (format === 'svg') {
            const blob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `diagram-${currentDiagramMeta?.name || 'export'}.svg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } else {
            // PNG or JPG
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();

            const svgBlob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            img.onload = () => {
                canvas.width = w;
                canvas.height = h;

                // For JPG we need a background
                if (format === 'jpg') {
                    ctx.fillStyle = theme.canvasBackground || '#ffffff';
                    ctx.fillRect(0, 0, w, h);
                }

                ctx.drawImage(img, 0, 0);

                const dataUrl = canvas.toDataURL(format === 'jpg' ? 'image/jpeg' : 'image/png', 0.9);
                const link = document.createElement('a');
                link.href = dataUrl;
                link.download = `diagram-${currentDiagramMeta?.name || 'export'}.${format}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);

                URL.revokeObjectURL(url);
            };

            img.onerror = (err) => {
                console.error('Export error:', err);
                alert('Ошибка при экспорте изображения');
                URL.revokeObjectURL(url);
            };

            img.src = url;
        }
    }, [elements, currentDiagramMeta, theme.canvasBackground, canvasRef]);

    // Keep a stable ref to handleLoadDiagram so the load effect below does not
    // re-fire every render. clearSelection (inside handleLoadDiagram) is not
    // wrapped in useCallback in useSelection, making handleLoadDiagram itself
    // unstable. Using a ref breaks the dependency chain.
    const handleLoadDiagramRef = useRef(handleLoadDiagram);
    handleLoadDiagramRef.current = handleLoadDiagram;

    // Автозагрузка по ID — fires only when the diagram ID changes
    useEffect(() => {
        if (!initialDiagramId) return;
        let cancelled = false;
        const reqId = Date.now();
        loadReqIdRef.current = reqId;
        setCurrentDiagramId(null);
        userModifiedRef.current = false;
        (async () => {
            try {
                const diagram = await getDiagramById(projectId, initialDiagramId);
                if (cancelled) return;

                if (loadReqIdRef.current === reqId && !userModifiedRef.current) {
                    handleLoadDiagramRef.current(diagram, initialDiagramId);
                    onDiagramLoaded?.(diagram, { diagramId: initialDiagramId, projectId });
                }
            } catch (e) {
                console.error('Не удалось загрузить диаграмму', e);
                if (!cancelled && loadReqIdRef.current === reqId) {
                    onDiagramLoadFailed?.({ diagramId: initialDiagramId, projectId, error: e });
                }
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialDiagramId, projectId]);

    return {
        currentDiagramId,
        setCurrentDiagramId,
        currentDiagramMeta,
        setCurrentDiagramMeta,
        diagramGitlabPath,
        setDiagramGitlabPath,
        handleSaveDiagram,
        handlePublishDiagram,
        handleLoadDiagram,
        handleExportImage,
        isSaving,
        isPublishing,
        code,
        setCode
    };
};
